import { sha256Hex } from "./model-cache";
import {
  ChunkAccumulator,
  VolumeError,
  checkChunkResponse,
  chunkByteRange,
  decideFinalize,
  makeChunkLayout,
  parseVolumeArtifact,
  planVolumeResume,
  resolveVolumeChunkSize,
  type ChunkHash,
  type VolumeArtifact,
  type VolumeCheckpoint,
  type VolumeCheckpointStore,
  type VolumeSink,
} from "./volume-core";
import { createOpfsVolumeSink } from "./volume-sink";

export type VolumePhase =
  | "idle"
  | "downloading"
  | "paused"
  | "verifying"
  | "ready"
  | "error";

export interface VolumeProgress {
  volume: string;
  phase: VolumePhase;
  bytes: number;
  total: number;
  /** bytes/sec */
  rate: number;
  /** 남은 초. 계산 불가면 null */
  eta: number | null;
  chunksDone: number;
  chunksTotal: number;
  source: "network" | "file";
  error?: string;
}

export interface VolumeDownloadOptions {
  /** 논리 이름. 예: "statute-core" */
  name: string;
  /** OPFS 파일명. 기본 `<name>.aknpkg` */
  fileName?: string;
  /** 없으면 에어갭 가져오기(importFile)만 가능하다. */
  artifact?: VolumeArtifact;
  store: VolumeCheckpointStore;
  /** 테스트/커스텀 저장소. 없으면 OPFS sink를 만든다. */
  sink?: VolumeSink;
  onProgress?: (progress: VolumeProgress) => void;
  fetchImpl?: typeof fetch;
  now?: () => number;
  /** 8~16MB. artifact.chunkSize와 다르면 시작 전에 실패한다. */
  chunkSize?: number;
}

const USER_NETWORK_ERROR =
  "다운로드를 마치지 못했습니다. 네트워크를 확인한 뒤 다시 시도해 주세요.";
const USER_HASH_ERROR = "받은 데이터가 손상되었습니다. 다시 받아 주세요.";
const USER_STORAGE_ERROR = "기기에 저장하지 못했습니다. 디스크 여유 공간을 확인해 주세요.";
const USER_MANIFEST_ERROR = "배포 정보를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.";
const USER_UNAVAILABLE_ERROR = "아직 배포되지 않은 데이터입니다.";

function isAbortError(error: unknown): boolean {
  return (error as DOMException | undefined)?.name === "AbortError";
}

function userMessage(error: unknown): string {
  if (error instanceof VolumeError) {
    switch (error.code) {
      case "hash":
      case "size":
        return USER_HASH_ERROR;
      case "storage":
        return USER_STORAGE_ERROR;
      case "manifest":
      case "range":
        return USER_MANIFEST_ERROR;
      case "unavailable":
        return USER_UNAVAILABLE_ERROR;
      default:
        return USER_NETWORK_ERROR;
    }
  }
  return USER_NETWORK_ERROR;
}

/**
 * 코퍼스 볼륨 스트리밍 다운로더.
 *
 * - 8~16MB Range → OPFS `.part` (메모리에는 청크 1개만)
 * - 청크 SHA-256을 매니페스트와 대조, 최종 verifying에서 디스크를 다시 읽어 재검증
 * - 체크포인트(바이트/청크 해시)로 reload 후 이어받기
 * - 완료 시 `.part` → 최종 이름 rename, `isReady()`로 확인
 */
export class VolumeDownloader {
  private readonly options: VolumeDownloadOptions;
  private readonly fileName: string;
  private readonly partName: string;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;

  private controller: AbortController | null = null;
  private running: Promise<void> | null = null;
  private importing: Promise<void> | null = null;
  private sinkPromise: Promise<VolumeSink> | null = null;
  private ownsSink = false;
  private paused = false;
  private cancelled = false;

  private progress: VolumeProgress;
  private rate = 0;
  private sampleBytes = 0;
  private sampleTime = 0;
  private lastEmit = 0;

  constructor(options: VolumeDownloadOptions) {
    this.options = options;
    this.fileName = options.fileName ?? `${options.name}.aknpkg`;
    this.partName = `${this.fileName}.part`;
    this.fetchImpl = options.fetchImpl ?? fetch;
    this.now = options.now ?? (() => Date.now());
    this.progress = {
      volume: options.name,
      phase: "idle",
      bytes: 0,
      total: options.artifact?.size ?? 0,
      rate: 0,
      eta: null,
      chunksDone: 0,
      chunksTotal: options.artifact?.chunks.length ?? 0,
      source: "network",
    };
  }

  get current(): VolumeProgress {
    return this.progress;
  }

  get name(): string {
    return this.options.name;
  }

  get readyFile(): string {
    return this.fileName;
  }

  private emit(patch: Partial<VolumeProgress>): void {
    this.progress = { ...this.progress, ...patch };
    this.options.onProgress?.(this.progress);
  }

  private checkpointKey(): string {
    return `${this.fileName}|${this.options.artifact?.sha256 ?? "local"}`;
  }

  private sink(): Promise<VolumeSink> {
    if (this.options.sink) return Promise.resolve(this.options.sink);
    if (!this.sinkPromise) {
      this.ownsSink = true;
      this.sinkPromise = createOpfsVolumeSink().then(({ sink }) => sink);
    }
    return this.sinkPromise;
  }

  /** OPFS에 최종 파일이 기대 크기로 존재하는지. */
  async isReady(name: string = this.fileName): Promise<boolean> {
    const expected = this.options.artifact?.size;
    try {
      const size = await (await this.sink()).size(name);
      return expected === undefined ? size !== null && size > 0 : size === expected;
    } catch (error) {
      console.warn("[beoptong] volume readiness check failed", error);
      return false;
    }
  }

  pause(): void {
    this.paused = true;
    this.controller?.abort();
  }

  start(): Promise<void> {
    if (this.running) return this.running;
    this.cancelled = false;
    this.paused = false;
    this.running = this.run().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  /** 일시정지가 아니라 취소. `.part`와 체크포인트를 지운다. */
  async cancel(): Promise<void> {
    this.cancelled = true;
    this.paused = false;
    this.controller?.abort();
    await Promise.allSettled([this.running, this.importing]);
    try {
      const sink = await this.sink();
      await sink.remove(this.partName);
      await sink.flush(this.partName);
    } catch (error) {
      console.warn("[beoptong] volume cancel cleanup failed", error);
    }
    await this.options.store.clear(this.checkpointKey()).catch(() => undefined);
    this.emit({
      phase: "idle",
      bytes: 0,
      rate: 0,
      eta: null,
      chunksDone: 0,
      error: undefined,
    });
  }

  async dispose(): Promise<void> {
    this.pause();
    await Promise.allSettled([this.running, this.importing]);
    if (this.ownsSink && this.sinkPromise) {
      const sink = await this.sinkPromise.catch(() => null);
      await sink?.close().catch(() => undefined);
    }
  }

  private resetRate(bytes: number): void {
    this.rate = 0;
    this.sampleBytes = bytes;
    this.sampleTime = this.now();
    this.lastEmit = 0;
  }

  private reportBytes(
    bytes: number,
    chunksDone: number,
    chunksTotal: number,
    force = false,
  ): void {
    const total = this.options.artifact?.size ?? this.progress.total;
    const now = this.now();
    const dt = now - this.sampleTime;
    if (dt >= 400) {
      const delta = bytes - this.sampleBytes;
      if (delta >= 0) this.rate = delta / (dt / 1000);
      this.sampleBytes = bytes;
      this.sampleTime = now;
    }
    if (!Number.isFinite(this.rate) || this.rate < 0) this.rate = 0;
    if (!force && now - this.lastEmit < 200) return;
    this.lastEmit = now;
    const eta = this.rate > 0 ? Math.max(0, (total - bytes) / this.rate) : null;
    this.emit({
      phase: "downloading",
      bytes,
      total,
      rate: this.rate,
      eta,
      chunksDone,
      chunksTotal,
    });
  }

  private requireArtifact(): VolumeArtifact {
    const artifact = this.options.artifact;
    if (!artifact) throw new VolumeError("unavailable", "volume artifact required");
    return artifact;
  }

  private async run(): Promise<void> {
    const artifact = this.requireArtifact();
    if (this.options.chunkSize !== undefined && this.options.chunkSize !== artifact.chunkSize) {
      throw new VolumeError("manifest", "chunkSize override does not match artifact");
    }
    resolveVolumeChunkSize(artifact.chunkSize);

    let sink: VolumeSink;
    try {
      sink = await this.sink();
    } catch (error) {
      this.emit({ phase: "error", rate: 0, eta: null, error: USER_STORAGE_ERROR });
      throw error;
    }

    const key = this.checkpointKey();

    try {
      if (await this.isReady()) {
        this.emit({
          phase: "ready",
          bytes: artifact.size,
          total: artifact.size,
          rate: 0,
          eta: 0,
          chunksDone: artifact.chunks.length,
          chunksTotal: artifact.chunks.length,
          error: undefined,
        });
        return;
      }

      const checkpoint = await this.options.store.get(key);
      const partSize = await sink.size(this.partName);
      const plan = planVolumeResume({ artifact, checkpoint, partSize });
      if (plan.restart) {
        if (partSize !== null) await sink.remove(this.partName);
        if (checkpoint) await this.options.store.clear(key);
      } else if (partSize !== null && partSize > plan.truncateTo) {
        // 체크포인트 기록 전에 쓴 꼬리는 검증되지 않았으므로 버린다.
        await sink.truncate(this.partName, plan.truncateTo);
      }

      if (this.paused) {
        this.emit({ phase: "paused", rate: 0, eta: null });
        return;
      }

      const accumulator = new ChunkAccumulator(artifact.chunks, plan.completedChunks);
      this.resetRate(plan.bytesDone);
      const total = artifact.size;
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.emit({
        phase: "downloading",
        source: "network",
        bytes: plan.bytesDone,
        total,
        rate: 0,
        eta: null,
        chunksDone: plan.completedChunks,
        chunksTotal: artifact.chunks.length,
        error: undefined,
      });

      for (let index = plan.completedChunks; index < artifact.chunks.length; index++) {
        if (signal.aborted) throw new VolumeError("aborted", "paused");
        const range = chunkByteRange(artifact.chunks, index);
        const bytes = await this.fetchChunk(
          artifact.url,
          range,
          artifact.chunks[index]!.sha256,
          signal,
          (received) => {
            this.reportBytes(
              range.start + received,
              index,
              artifact.chunks.length,
            );
          },
        );
        const sha256 = await sha256Hex(bytes);
        accumulator.record(index, bytes.byteLength, sha256);
        await sink.write(this.partName, range.start, bytes);
        await sink.flush(this.partName);

        const state = accumulator.state;
        await this.options.store.set(key, {
          sha256: artifact.sha256,
          size: artifact.size,
          chunkSize: artifact.chunkSize,
          bytesDone: state.bytesDone,
          completedChunks: state.completedChunks,
          chunkHashes: state.chunkHashes,
          updatedAt: this.now(),
        });
        this.reportBytes(state.bytesDone, state.completedChunks, artifact.chunks.length, true);
      }

      await this.verifyPart(sink, artifact, signal);
      await this.finalize(sink, artifact);
    } catch (error) {
      if (this.cancelled) return;
      if (this.paused || isAbortError(error)) {
        this.emit({ phase: "paused", rate: 0, eta: null });
        return;
      }
      if (error instanceof VolumeError && (error.code === "hash" || error.code === "size")) {
        // 손상된 .part는 이어받기로 복구할 수 없다. 다음 시도는 처음부터.
        await sink.remove(this.partName).catch(() => undefined);
        await this.options.store.clear(key).catch(() => undefined);
      }
      console.error("[beoptong] volume download failed", this.options.name, error);
      this.emit({ phase: "error", rate: 0, eta: null, error: userMessage(error) });
      throw error;
    } finally {
      this.controller = null;
    }
  }

  private async verifyPart(
    sink: VolumeSink,
    artifact: VolumeArtifact,
    signal?: AbortSignal,
  ): Promise<void> {
    this.emit({
      phase: "verifying",
      bytes: 0,
      total: artifact.size,
      rate: 0,
      eta: null,
      chunksDone: 0,
      chunksTotal: artifact.chunks.length,
    });

    for (const chunk of artifact.chunks) {
      if (signal?.aborted) throw new VolumeError("aborted", "paused");
      const range = chunkByteRange(artifact.chunks, chunk.index);
      const bytes = await sink.read(this.partName, range.start, range.length);
      if (bytes.byteLength !== range.length) {
        throw new VolumeError("size", `chunk ${chunk.index} read back short`);
      }
      const sha256 = await sha256Hex(bytes);
      if (sha256 !== chunk.sha256) {
        throw new VolumeError("hash", `chunk ${chunk.index} on-disk hash mismatch`);
      }
      this.emit({
        phase: "verifying",
        bytes: range.start + range.length,
        chunksDone: chunk.index + 1,
      });
    }
  }

  private async finalize(sink: VolumeSink, artifact: VolumeArtifact): Promise<void> {
    const partSize = await sink.size(this.partName);
    const existingSize = await sink.size(this.fileName);
    const action = decideFinalize({
      verified: true,
      partSize: partSize ?? -1,
      expectedSize: artifact.size,
      existingSize,
    });

    if (action === "keep-existing") {
      await sink.remove(this.partName);
    } else if (action === "promote") {
      await sink.promote(this.partName, this.fileName);
    } else {
      throw new VolumeError("storage", "volume finalize failed");
    }

    await this.options.store.clear(this.checkpointKey());
    this.emit({
      phase: "ready",
      bytes: artifact.size,
      total: artifact.size,
      rate: 0,
      eta: 0,
      chunksDone: artifact.chunks.length,
      chunksTotal: artifact.chunks.length,
      error: undefined,
    });
  }

  private async fetchChunk(
    url: string,
    range: ReturnType<typeof chunkByteRange>,
    expectedSha: string,
    signal: AbortSignal,
    onBytes: (received: number) => void,
  ): Promise<Uint8Array> {
    let response: Response;
    try {
      response = await this.fetchImpl(url, {
        headers: { Range: `bytes=${range.start}-${range.end}` },
        signal,
        cache: "no-store",
      });
    } catch (error) {
      if (isAbortError(error)) throw new VolumeError("aborted", "paused");
      throw new VolumeError("network", "range fetch failed");
    }

    const check = checkChunkResponse({
      status: response.status,
      contentRange: response.headers.get("content-range"),
      contentLength: response.headers.get("content-length"),
      expectedStart: range.start,
      expectedEnd: range.end,
      expectedTotal: range.total,
    });
    if (!check.ok) {
      throw new VolumeError("range", `chunk ${range.index}: ${check.reason}`);
    }

    const bytes = await this.readBody(response, range.length, onBytes);
    if (bytes.byteLength !== range.length) {
      throw new VolumeError("size", `chunk ${range.index} truncated`);
    }
    const sha256 = await sha256Hex(bytes);
    if (sha256 !== expectedSha) {
      throw new VolumeError("hash", `chunk ${range.index} hash mismatch`);
    }
    return bytes;
  }

  private async readBody(
    response: Response,
    length: number,
    onBytes: (received: number) => void,
  ): Promise<Uint8Array> {
    const reader = response.body?.getReader();
    if (!reader) {
      const buffer = new Uint8Array(await response.arrayBuffer());
      onBytes(buffer.byteLength);
      return buffer;
    }

    const out = new Uint8Array(length);
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (received + value.byteLength > length) {
        await reader.cancel().catch(() => undefined);
        throw new VolumeError("size", "chunk overflow");
      }
      out.set(value, received);
      received += value.byteLength;
      onBytes(received);
    }
    return out.subarray(0, received);
  }

  /**
   * 에어갭: 사용자가 고른 `File`을 OPFS로 스트리밍한다.
   * artifact가 있으면 크기/청크 해시를 검증하고, 없으면(미배포 볼륨) 크기만 확인한다.
   */
  importFile(file: File): Promise<void> {
    if (this.importing) return this.importing;
    if (this.running) return Promise.reject(new VolumeError("storage", "download already running"));
    this.importing = this.runImport(file).finally(() => {
      this.importing = null;
    });
    return this.importing;
  }

  private async runImport(file: File): Promise<void> {
    const artifact = this.options.artifact;
    if (artifact && file.size !== artifact.size) {
      throw new VolumeError("size", `file size ${file.size} != ${artifact.size}`);
    }

    this.cancelled = false;
    this.paused = false;
    const sink = await this.sink();
    const chunkSize = artifact?.chunkSize ?? resolveVolumeChunkSize(this.options.chunkSize);
    const layout: ChunkHash[] = artifact?.chunks ?? makeChunkLayout(file.size, chunkSize);
    const controller = new AbortController();
    this.controller = controller;

    try {
      await sink.remove(this.partName);
      await this.options.store.clear(this.checkpointKey());
      this.resetRate(0);
      this.emit({
        phase: "downloading",
        source: "file",
        bytes: 0,
        total: file.size,
        rate: 0,
        eta: null,
        chunksDone: 0,
        chunksTotal: layout.length,
        error: undefined,
      });

      let position = 0;
      for (let index = 0; index < layout.length; index++) {
        if (controller.signal.aborted) throw new VolumeError("aborted", "cancelled");
        const chunk = layout[index]!;
        const bytes = await this.readFileBulk(file, index, layout);
        if (bytes.byteLength !== chunk.size) {
          throw new VolumeError("size", `file chunk ${index} short`);
        }
        const sha256 = await sha256Hex(bytes);
        if (artifact && sha256 !== chunk.sha256) {
          throw new VolumeError("hash", `file chunk ${index} hash mismatch`);
        }
        await sink.write(this.partName, position, bytes);
        await sink.flush(this.partName);
        position += chunk.size;
        this.reportBytes(position, index + 1, layout.length, true);
      }

      if (artifact) await this.verifyPart(sink, artifact, controller.signal);
      else {
        this.emit({
          phase: "verifying",
          bytes: file.size,
          total: file.size,
          rate: 0,
          eta: null,
          chunksDone: layout.length,
          chunksTotal: layout.length,
        });
      }
      const target: VolumeArtifact =
        artifact ?? {
          url: "",
          size: file.size,
          sha256: "",
          chunkSize,
          chunks: layout,
        };
      await this.finalize(sink, target);
    } catch (error) {
      if (this.cancelled) {
        await sink.remove(this.partName).catch(() => undefined);
        this.emit({ phase: "idle", bytes: 0, rate: 0, eta: null, chunksDone: 0 });
        throw new VolumeError("cancelled", "import cancelled");
      }
      if (this.paused) {
        this.emit({ phase: "paused", rate: 0, eta: null });
        throw new VolumeError("aborted", "import paused");
      }
      if (error instanceof VolumeError && (error.code === "hash" || error.code === "size")) {
        await sink.remove(this.partName).catch(() => undefined);
      }
      console.error("[beoptong] volume import failed", this.options.name, error);
      this.emit({ phase: "error", rate: 0, eta: null, error: userMessage(error) });
      throw error;
    } finally {
      this.controller = null;
    }
  }

  /** `File`은 랜덤 액세스가 가능하므로 청크마다 slice를 읽는다(메모리 8MB). */
  private async readFileBulk(file: File, index: number, layout: ChunkHash[]): Promise<Uint8Array> {
    let offset = 0;
    for (let i = 0; i < index; i++) offset += layout[i]!.size;
    const chunk = layout[index]!;
    return new Uint8Array(await file.slice(offset, offset + chunk.size).arrayBuffer());
  }
}

/** manifest에서 코퍼스 artifact를 읽는다. pending/누락이면 `VolumeError("unavailable")`. */
export async function fetchVolumeArtifact(
  manifestUrl: string,
  key: string,
  fetchImpl: typeof fetch = fetch,
): Promise<VolumeArtifact> {
  let response: Response;
  try {
    response = await fetchImpl(manifestUrl, { cache: "no-store" });
  } catch {
    throw new VolumeError("network", `manifest fetch failed: ${key}`);
  }
  if (!response.ok) {
    throw new VolumeError("manifest", `manifest ${response.status}: ${key}`);
  }
  let document: unknown;
  try {
    document = await response.json();
  } catch {
    throw new VolumeError("manifest", "manifest is not json");
  }
  return parseVolumeArtifact(document, key);
}

/** 테스트/데모용. 탭을 닫으면 사라진다. */
export class MemoryVolumeCheckpointStore implements VolumeCheckpointStore {
  private readonly map = new Map<string, VolumeCheckpoint>();

  async get(key: string): Promise<VolumeCheckpoint | null> {
    return this.map.get(key) ?? null;
  }

  async set(key: string, checkpoint: VolumeCheckpoint): Promise<void> {
    this.map.set(key, checkpoint);
  }

  async clear(key: string): Promise<void> {
    this.map.delete(key);
  }
}

/** 문자열 KV 어댑터. 확장에서는 chrome.storage.local을 감싼다. */
export interface VolumeCheckpointKV {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export class KeyValueVolumeCheckpointStore implements VolumeCheckpointStore {
  private readonly kv: VolumeCheckpointKV;

  constructor(kv: VolumeCheckpointKV) {
    this.kv = kv;
  }

  async get(key: string): Promise<VolumeCheckpoint | null> {
    try {
      const raw = await this.kv.get(key);
      return raw ? (JSON.parse(raw) as VolumeCheckpoint) : null;
    } catch {
      return null;
    }
  }

  async set(key: string, checkpoint: VolumeCheckpoint): Promise<void> {
    await this.kv.set(key, JSON.stringify(checkpoint));
  }

  async clear(key: string): Promise<void> {
    await this.kv.remove(key);
  }
}

/** 웹 하네스용. 확장에서는 chrome.storage.local 어댑터를 쓴다. */
export class LocalStorageVolumeCheckpointStore implements VolumeCheckpointStore {
  private readonly prefix: string;

  constructor(prefix = "beoptong:volume:") {
    this.prefix = prefix;
  }

  async get(key: string): Promise<VolumeCheckpoint | null> {
    try {
      const raw = localStorage.getItem(this.prefix + key);
      return raw ? (JSON.parse(raw) as VolumeCheckpoint) : null;
    } catch {
      return null;
    }
  }

  async set(key: string, checkpoint: VolumeCheckpoint): Promise<void> {
    localStorage.setItem(this.prefix + key, JSON.stringify(checkpoint));
  }

  async clear(key: string): Promise<void> {
    localStorage.removeItem(this.prefix + key);
  }
}
